import React, { useEffect, useState } from 'react';
import { AbacHeader } from './AbacHeader';
import { AbacSidebar, AttributeType } from './AbacSidebar';
import { AbacContent } from './AbacContent';
import { AbacCollection, PermissionCollection } from './AbacCollection';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getAllRolesAsync } from '@/data/store/actions';
import { TAbac } from '@/data/types';

type ViewMode = 'collection' | 'editor' | 'permissions';

const AbacMain: React.FC<{ onSave?: (abac: TAbac[]) => void; initialAbac?: TAbac[] }> = ({ onSave, initialAbac }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('collection');
  const [selectedAttribute, setSelectedAttribute] = useState<AttributeType>('roles');
  const [selectedCounts, setSelectedCounts] = useState({
    roles: 0,
    week_days: 0,
    month_days: 0,
    hours: 0,
  });
  const [currentSelections, setCurrentSelections] = useState<TAbac>({});
  const [collections, setCollections] = useState<PermissionCollection[]>(
    initialAbac
      ? initialAbac.map((abac, index) => {
          return { id: `collection-${index}`, attributes: abac };
        })
      : [],
  );
  const [editingCollection, setEditingCollection] = useState<PermissionCollection | null>(null);

  const handleAttributeSelect = (attribute: AttributeType) => {
    setSelectedAttribute(attribute);
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getAllRolesAsync());
  }, []);

  const handleSelectionChange = (attribute: AttributeType, selections: any) => {
    setSelectedCounts((prev) => ({
      ...prev,
      [attribute]: Array.isArray(selections) ? selections.length : selections ? 1 : 0,
    }));

    // Update current selections
    const newSelections = { ...currentSelections };
    switch (attribute) {
      case 'roles':
        newSelections.roles = selections;
        break;
      case 'week_days':
        newSelections.week_days = selections;
        break;
      case 'month_days':
        newSelections.month_days = selections;
        break;
      case 'hours':
        newSelections.hours = selections;
        break;
    }
    setCurrentSelections(newSelections);
  };

  const handleAddNew = () => {
    setEditingCollection(null);
    setViewMode('editor');
    // Reset selections
    setCurrentSelections({});
    setSelectedCounts({
      roles: 0,
      week_days: 0,
      month_days: 0,
      hours: 0,
    });
  };

  const handleEdit = (collection: PermissionCollection) => {
    setEditingCollection(collection);
    setViewMode('editor');
    // Load existing selections
    setCurrentSelections({
      roles: collection.attributes.roles,
      week_days: collection.attributes.week_days,
      month_days: collection.attributes.month_days,
      hours: collection.attributes.hours,
      action: collection.attributes.action,
      private: collection.attributes.private,
    });
    setSelectedCounts({
      roles: collection.attributes.roles ? collection.attributes.roles.length : 0,
      week_days: collection.attributes.week_days ? collection.attributes.week_days.length : 0,
      month_days: collection.attributes.month_days ? collection.attributes.month_days.length : 0,
      hours: collection.attributes.hours ? collection.attributes.hours.length : 0,
    });
  };

  const handleDelete = (collectionId: string) => {
    setCollections((prev) => prev.filter((c) => c.id !== collectionId));
  };

  const handleSetPermissions = () => {
    setViewMode('permissions');
  };

  const handleSavePermissions = (name: string, description?: string) => {
    const newCollection: PermissionCollection = {
      id: editingCollection?.id || `collection-${Date.now()}`,
      attributes: {
        roles: currentSelections.roles,
        week_days: currentSelections.week_days,
        month_days: currentSelections.month_days,
        hours: currentSelections.hours,
        action: currentSelections.action,
        private: currentSelections.private,
      },
    };

    if (editingCollection) {
      // Update existing collection
      setCollections((prev) => prev.map((c) => (c.id === editingCollection.id ? newCollection : c)));
    } else {
      // Add new collection
      setCollections((prev) => [...prev, newCollection]);
    }

    setViewMode('collection');
  };

  const handleCancel = () => {
    setViewMode('collection');
    setEditingCollection(null);
  };

  const handleSaveCollections = () => {
    const attributes = collections.map((collection) => collection.attributes);
    console.log(attributes);
    onSave?.(attributes);
    setViewMode('collection');
  };

  if (viewMode === 'collection') {
    return (
      <div className="mx-auto h-[95vh] w-[90rem] max-w-[95%] overflow-hidden rounded-2xl bg-white shadow-2xl md:h-[90vh]">
        <AbacCollection
          collections={collections}
          onAddNew={handleAddNew}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSave={handleSaveCollections}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto h-[95vh] w-[90rem] max-w-[95%] overflow-hidden rounded-2xl bg-white shadow-2xl md:h-[90vh]">
      <div className="bg-background flex h-full flex-col">
        <AbacHeader
          onSetPermissions={handleSetPermissions}
          onSavePermissions={handleSavePermissions}
          onCancel={handleCancel}
          isEditing={!!editingCollection}
          viewMode={viewMode}
        />
        <div className="flex flex-1 overflow-hidden">
          {viewMode === 'editor' && (
            <AbacSidebar
              selectedAttribute={selectedAttribute}
              onAttributeSelect={handleAttributeSelect}
              selectedCounts={selectedCounts}
            />
          )}
          <AbacContent
            selectedAttribute={selectedAttribute}
            onSelectionChange={handleSelectionChange}
            currentSelections={currentSelections}
            onActionChange={(action) => setCurrentSelections((prev) => ({ ...prev, action }))}
            onPrivateChange={(isPrivate) => setCurrentSelections((prev) => ({ ...prev, isPrivate }))}
            viewMode={viewMode}
            onSetPermissions={handleSetPermissions}
            onSavePermissions={handleSavePermissions}
          />
        </div>
      </div>
    </div>
  );
};

export default AbacMain;
